import { useEffect, useState } from "react";
import { isBefore, isAfter, addDays, subDays } from "date-fns";

import { useTheme } from "@mui/material/styles";

import useData from "../../hooks/useData";
import useSurvey from "../../hooks/useSurvey";

import { Box, Stack, Typography } from "@mui/material";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";

import SurveyEntry_Section from "./SurveyEntry_Section";

import PageStatus from "./PageStatus";

const SurveyEntry_Page = ({
  callResultId,
  page,
  sections,
  questions,
  answers,
  call,
  location,
  onChange,
  onNextPage,
  onPrevPage,
  allowPrev,
  allowNext,
  isDisabled,
  testMode = false,
}) => {
  const theme = useTheme();
  const { sortByFields } = useData();
  const { getSectionVariabilityData, showSection } = useSurvey();

  const [pageSections, setPageSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(null);

  useEffect(() => {
    getActiveSections();
  }, [page, sections, answers, location]);

  const getActiveSections = async () => {
    if (!page || !sections || !Array.isArray(sections)) {
      setPageSections([]);
      setCurrentSection(null);
      return;
    }

    let list = [];
    let newSectionNumber = 0;
    for (const section of sections) {
      if (section.surveyPageId !== page.id) continue;
      const show = await showSection({ section, answers, location });
      if (!show) continue;

      list.push({ ...section, sectionNumber: newSectionNumber });
      newSectionNumber++;
    }

    const sorted = sortByFields(list, ["sectionNumber"]);
    setCurrentSection((current) => {
      const stillVisible = current
        ? sorted.find((section) => section.id === current.id)
        : null;

      return stillVisible || (sorted.length > 0 ? sorted[0] : null);
    });

    setPageSections(sorted);
  };

  const handleNextSection = () => {
    setCurrentSection((current) => {
      if (!current || current.sectionNumber === pageSections.length - 1)
        return current;

      return pageSections[current.sectionNumber + 1];
    });
  };

  const handlePrevSection = () => {
    setCurrentSection((current) => {
      if (!current || current.sectionNumber === 0) return current;

      return pageSections[current.sectionNumber - 1];
    });
  };

  const allowPrevSection = () => {
    if (!currentSection || currentSection.sectionNumber === 0) return false;
    return true;
  };

  const allowNextSection = () => {
    if (
      !currentSection ||
      currentSection.sectionNumber === pageSections.length - 1
    )
      return false;
    return true;
  };

  return (
    <Stack>
      <Box
        sx={{
          paddingLeft: "4px",
          backgroundColor: theme.palette.primary.main,
          position: "sticky",
          top: 0,
          zIndex: 250,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: "4px",
            paddingBottom: "4px",
            paddingRight: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ArrowCircleLeftIcon
              onClick={onPrevPage}
              color="black"
              sx={{ cursor: "pointer", opacity: allowPrev() ? 1 : 0.1 }}
            />
            <Box sx={{ display: "flex" }}>
              <PageStatus
                page={page}
                sections={sections}
                questions={questions}
                answers={answers}
                location={location}
                call={call}
              />
              <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                {page.name}
              </Typography>
            </Box>
          </Box>
          <ArrowCircleRightIcon
            onClick={onNextPage}
            color="black"
            sx={{ cursor: "pointer", opacity: allowNext() ? 1 : 0.1 }}
          />
        </Box>
      </Box>

      {currentSection && (
        <SurveyEntry_Section
          key={currentSection.id}
          callResultId={callResultId}
          section={currentSection}
          questions={questions}
          answers={answers}
          call={call}
          location={location}
          onChange={onChange}
          onNextSection={handleNextSection}
          onPrevSection={handlePrevSection}
          allowPrev={allowPrevSection}
          allowNext={allowNextSection}
          isDisabled={isDisabled}
          testMode={testMode}
        />
      )}
    </Stack>
  );
};

export default SurveyEntry_Page;
